import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CurrencyCode } from '@/shared/lib/currency';

import { RollsRound, RollsRoundHistory,RollsState, RollsStatus } from './types';

const initialState: RollsState = {
  currentRound: {
    id: '',
    index: 0,
    status: RollsStatus.WAITING,
    players: [],
    totalPot: 0,
    potsByCurrency: {},
    currencyMode: 'TON',
    fxRates: {},
    winnerId: null,
    countdownSecondsLeft: null,
    spinTargetAngle: null,
    serverSeedHash: null,
  },
  onlineCounts: {},
  selectedCurrency: 'STARS',
  autoplay: {
    isActive: false,
    betAmount: 0.1,
    numberOfGames: 10,
    gamesPlayed: 0,
  },
};

function mapStatus(status: string): RollsStatus {
  const map: Record<string, RollsStatus> = {
    waiting: RollsStatus.WAITING,
    countdown: RollsStatus.COUNTDOWN,
    spinning: RollsStatus.SPINNING,
    finished: RollsStatus.FINISHED,
  };
  return map[status] || RollsStatus.WAITING;
}

const rollsSlice = createSlice({
  name: 'rolls',
  initialState,
  reducers: {
    // ── Server-driven: full round state from backend ──
    setRound(state, action: PayloadAction<RollsRound>) {
      const data = action.payload;
      state.currentRound = {
        ...data,
        status: mapStatus(data.status as string),
      };
    },

    // Countdown tick from WS — updates secondsLeft + players
    wsCountdownTick(state, action: PayloadAction<{ secondsLeft: number } & Partial<RollsRound>>) {
      state.currentRound.countdownSecondsLeft = action.payload.secondsLeft;
      if (action.payload.players) {
        state.currentRound.players = action.payload.players;
        state.currentRound.totalPot = action.payload.totalPot ?? state.currentRound.totalPot;
        state.currentRound.potsByCurrency =
          action.payload.potsByCurrency ?? state.currentRound.potsByCurrency;
        state.currentRound.currencyMode =
          action.payload.currencyMode ?? state.currentRound.currencyMode;
        state.currentRound.fxRates = action.payload.fxRates ?? state.currentRound.fxRates;
      }
      state.currentRound.status = RollsStatus.COUNTDOWN;
    },

    // Spin start from WS
    wsSpinStart(
      state,
      action: PayloadAction<{ winnerId: string; spinTargetAngle: number } & Partial<RollsRound>>,
    ) {
      state.currentRound.status = RollsStatus.SPINNING;
      state.currentRound.winnerId = action.payload.winnerId;
      state.currentRound.spinTargetAngle = action.payload.spinTargetAngle;
      state.currentRound.countdownSecondsLeft = null;
      if (action.payload.players) {
        state.currentRound.players = action.payload.players;
        state.currentRound.totalPot = action.payload.totalPot ?? state.currentRound.totalPot;
        state.currentRound.potsByCurrency =
          action.payload.potsByCurrency ?? state.currentRound.potsByCurrency;
        state.currentRound.currencyMode =
          action.payload.currencyMode ?? state.currentRound.currencyMode;
        state.currentRound.fxRates = action.payload.fxRates ?? state.currentRound.fxRates;
      }
    },

    // Round finished from WS
    wsRoundFinished(state, _action: PayloadAction<RollsRoundHistory>) {
      state.currentRound.status = RollsStatus.FINISHED;
    },

    // New round from WS
    wsNewRound(state, action: PayloadAction<RollsRound>) {
      const data = action.payload;
      state.currentRound = {
        ...data,
        status: mapStatus(data.status as string),
      };
    },

    // Online count from WS — per-game map { roulette: N, jetton: N, ... }
    wsOnlineCount(state, action: PayloadAction<Record<string, number>>) {
      state.onlineCounts = action.payload;
    },

    setSelectedCurrency(state, action: PayloadAction<CurrencyCode>) {
      state.selectedCurrency = action.payload;
    },

    // ── Autoplay ──
    startAutoplay(state, action: PayloadAction<{ betAmount: number; numberOfGames: number }>) {
      state.autoplay.isActive = true;
      state.autoplay.betAmount = action.payload.betAmount;
      state.autoplay.numberOfGames = action.payload.numberOfGames;
      state.autoplay.gamesPlayed = 0;
    },

    recordAutoplayGame(state) {
      state.autoplay.gamesPlayed += 1;
    },

    stopAutoplay(state) {
      state.autoplay.isActive = false;
      state.autoplay.gamesPlayed = 0;
    },

    // ── User actions (sagas listen to these) ──
    placeBet(
      _state,
      _action: PayloadAction<{ amount: number; currency: CurrencyCode; giftId?: string }>,
    ) {},
    requestAddBet(
      _state,
      _action: PayloadAction<{ amount: number; currency: CurrencyCode; giftId?: string }>,
    ) {},
  },
});

export const rollsActions = rollsSlice.actions;
export const rollsReducer = rollsSlice.reducer;
